import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PhotoIcon,
  TrashIcon,
  CheckCircleIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import FormInput from './product/FormInput';
import FormTextarea from './product/FormTextarea';
import FormSelect from './product/FormSelect';
import BrandSelector from './BrandSelector';

const EditProductModern = ({ isOpen, onClose, product, onProductUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    short_description: '',
    brand_id: null,
    category_id: '',
    price_b2c: '',
    price_b2b: '',
    cost_price: '',
    stock_quantity: '',
    stock_b2c: '',
    stock_b2b: '',
    min_stock_level: '10',
    is_active: true,
    is_featured: false
  });
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (isOpen && product) {
      loadProductData();
      fetchCategories();
    }
  }, [isOpen, product]);

  const loadProductData = () => {
    setFormData({
      name: product.name || '',
      sku: product.sku || '',
      description: product.description || '',
      short_description: product.short_description || '',
      brand_id: product.brand_id || null,
      category_id: product.category_id || '',
      price_b2c: product.price_b2c !== null && product.price_b2c !== undefined ? product.price_b2c : '',
      price_b2b: product.price_b2b !== null && product.price_b2b !== undefined ? product.price_b2b : '',
      cost_price: product.cost_price !== null && product.cost_price !== undefined ? product.cost_price : '',
      stock_quantity: product.stock_quantity !== null && product.stock_quantity !== undefined ? product.stock_quantity : '',
      stock_b2c: product.stock_b2c !== null && product.stock_b2c !== undefined ? product.stock_b2c : '',
      stock_b2b: product.stock_b2b !== null && product.stock_b2b !== undefined ? product.stock_b2b : '',
      min_stock_level: product.min_stock_level || '10',
      is_active: product.is_active !== false,
      is_featured: product.is_featured || false
    });

    const existingImages = product.images ? product.images.map((imageUrl, index) => ({
      id: `existing-${index}`,
      url: imageUrl.startsWith('http') ? imageUrl : `/api${imageUrl}`,
      isExisting: true
    })) : [];
    
    setImages(existingImages);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImageObjects = files.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      isExisting: false
    }));
    
    setNewImages(prev => [...prev, ...newImageObjects]);
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    setNewImages(prev => {
      const filtered = prev.filter(img => img.id !== imageId);
      prev.filter(img => img.id === imageId).forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
      return filtered;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name?.trim()) newErrors.name = 'שם המוצר הוא שדה חובה';
    if (!formData.sku?.trim()) newErrors.sku = 'SKU הוא שדה חובה';
    if (!formData.description?.trim()) newErrors.description = 'תיאור המוצר הוא שדה חובה';
    if (!formData.category_id) newErrors.category_id = 'יש לבחור קטגוריה';
    if (!formData.price_b2c || parseFloat(formData.price_b2c) <= 0) newErrors.price_b2c = 'מחיר B2C חייב להיות גדול מ-0';
    if (!formData.price_b2b || parseFloat(formData.price_b2b) <= 0) newErrors.price_b2b = 'מחיר B2B חייב להיות גדול מ-0';
    if (!formData.stock_quantity || parseInt(formData.stock_quantity) < 0) newErrors.stock_quantity = 'מלאי חייב להיות 0 או יותר';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('נא למלא את כל השדות הנדרשים');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      
      Object.keys(formData).forEach(key => {
        let value = formData[key];
        
        // Special handling for brand - always send it (even if empty to clear it)
        if (key === 'brand') {
          // Send special marker "__CLEAR_BRAND__" for empty brand
          const brandValue = (!value || value.trim() === '') ? '__CLEAR_BRAND__' : value;
          submitData.append(key, brandValue);
          return;
        }
        
        if (value === '' || value === null || value === undefined) return;
        submitData.append(key, typeof value === 'boolean' ? (value ? 'true' : 'false') : value);
      });

      if (images.length > 0) {
        const existingImagePaths = images.map(img => img.url.replace('/api', ''));
        submitData.append('existingImages', JSON.stringify(existingImagePaths));
      }

      newImages.forEach(img => {
        if (img.file && img.file.size > 0) {
          submitData.append('images', img.file);
        } else {
        }
      });

      const response = await axios.put(`/api/products/${product.id}`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('המוצר עודכן בהצלחה!');
      onProductUpdated(response.data.product);
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg || 
                          'שגיאה בעדכון המוצר';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const allImages = [...images, ...newImages];
  const mainImage = allImages[0]?.url || allImages[0]?.preview;
  const categoryName = categories.find(c => c.id === formData.category_id)?.name || 'ללא קטגוריה';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-white/20 rounded-xl backdrop-blur-sm flex items-center justify-center">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">עריכה מהירה</h2>
              <p className="text-white/80 text-sm">{product?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all text-white font-medium flex items-center gap-2"
            >
              <EyeIcon className="h-5 w-5" />
              {showPreview ? 'הסתר תצוגה' : 'הצג תצוגה'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-all text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - Split Screen */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Form */}
          <div className={`${showPreview ? 'w-1/2' : 'w-full'} overflow-y-auto p-6 border-l border-gray-200`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>📝</span> מידע בסיסי
                </h3>
                
                <div className="space-y-4">
                  <FormInput
                    label="שם המוצר"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={errors.name}
                    required
                    placeholder="שם המוצר..."
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="SKU"
                      name="sku"
                      value={formData.sku}
                      onChange={handleInputChange}
                      error={errors.sku}
                      required
                      placeholder="PERF-001"
                    />

                    <FormSelect
                      label="קטגוריה"
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      error={errors.category_id}
                      required
                      options={categories}
                      placeholder="בחר..."
                    />
                  </div>

                  <FormTextarea
                    label="תיאור קצר"
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="תיאור קצר..."
                  />

                  {/* Brand Selector */}
                  <BrandSelector
                    value={formData.brand_id}
                    onChange={(brandId) => setFormData(prev => ({ ...prev, brand_id: brandId }))}
                    error={errors.brand_id}
                    allowCreate={true}
                  />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>💰</span> תמחור
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <FormInput
                    label="B2C"
                    name="price_b2c"
                    type="number"
                    value={formData.price_b2c}
                    onChange={handleInputChange}
                    error={errors.price_b2c}
                    required
                    step="0.01"
                    placeholder="0.00"
                  />

                  <FormInput
                    label="B2B"
                    name="price_b2b"
                    type="number"
                    value={formData.price_b2b}
                    onChange={handleInputChange}
                    error={errors.price_b2b}
                    required
                    step="0.01"
                    placeholder="0.00"
                  />

                  <FormInput
                    label="עלות"
                    name="cost_price"
                    type="number"
                    value={formData.cost_price}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                {formData.cost_price && formData.price_b2c && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-sm opacity-90">רווח למוצר:</span>
                      <span className="text-2xl font-bold">
                        {((((formData.price_b2c - formData.cost_price) / formData.cost_price) * 100) || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Inventory Section */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>📦</span> מלאי
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <FormInput
                    label="כללי"
                    name="stock_quantity"
                    type="number"
                    value={formData.stock_quantity}
                    onChange={handleInputChange}
                    error={errors.stock_quantity}
                    required
                    placeholder="0"
                  />

                  <FormInput
                    label="B2C"
                    name="stock_b2c"
                    type="number"
                    value={formData.stock_b2c}
                    onChange={handleInputChange}
                    placeholder="0"
                  />

                  <FormInput
                    label="B2B"
                    name="stock_b2b"
                    type="number"
                    value={formData.stock_b2b}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Images Section */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>🖼️</span> תמונות
                </h3>
                
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-500 file:text-white hover:file:bg-orange-600 file:cursor-pointer"
                />

                {allImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    {allImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <img
                          src={image.url || image.preview}
                          alt="Product"
                          className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 group-hover:border-orange-400 transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description Section */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span>📋</span> תיאור מלא
                </h3>
                
                <FormTextarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={errors.description}
                  required
                  rows={6}
                  placeholder="תיאור מפורט של המוצר..."
                />
              </div>

              {/* Status Toggles */}
              <div className="flex gap-4 p-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl border-2 border-blue-300">
                <label className="flex items-center gap-3 cursor-pointer flex-1 bg-white rounded-lg p-3">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500 cursor-pointer"
                  />
                  <span className="text-sm font-bold text-gray-900">✅ מוצר פעיל</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer flex-1 bg-white rounded-lg p-3">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500 cursor-pointer"
                  />
                  <span className="text-sm font-bold text-gray-900">🔥 מוצר טרנד</span>
                </label>
              </div>
            </form>
          </div>

          {/* Right: Live Preview */}
          {showPreview && (
            <div className="w-1/2 overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 p-6">
              <div className="sticky top-0 mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-600 mb-4">
                  <EyeIcon className="h-5 w-5" />
                  תצוגה מקדימה
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
                {/* Main Image */}
                <div className="aspect-square bg-gray-200 rounded-xl overflow-hidden">
                  {mainImage ? (
                    <img src={mainImage} alt={formData.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <PhotoIcon className="h-24 w-24 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Badges */}
                <div className="flex gap-2 flex-wrap">
                  {formData.is_active && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                      ✅ פעיל
                    </span>
                  )}
                  {formData.is_featured && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">
                      🔥 טרנד
                    </span>
                  )}
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                    {categoryName}
                  </span>
                </div>

                {/* Product Name */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{formData.name || 'שם המוצר'}</h3>
                  <p className="text-sm text-gray-500 mt-1">{formData.sku || 'SKU'}</p>
                </div>

                {/* Short Description */}
                {formData.short_description && (
                  <p className="text-gray-600 leading-relaxed">{formData.short_description}</p>
                )}

                {/* Prices */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl p-4">
                    <p className="text-xs text-green-700 font-medium mb-1">מחיר B2C</p>
                    <p className="text-2xl font-bold text-green-900">
                      ₪{formData.price_b2c || '0.00'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl p-4">
                    <p className="text-xs text-blue-700 font-medium mb-1">מחיר B2B</p>
                    <p className="text-2xl font-bold text-blue-900">
                      ₪{formData.price_b2b || '0.00'}
                    </p>
                  </div>
                </div>

                {/* Stock */}
                <div className="bg-gray-100 rounded-xl p-4">
                  <p className="text-xs text-gray-600 font-medium mb-2">מלאי זמין</p>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xl font-bold text-gray-900">{formData.stock_quantity || 0}</p>
                      <p className="text-xs text-gray-500">כללי</p>
                    </div>
                    {formData.stock_b2c > 0 && (
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-700">{formData.stock_b2c}</p>
                        <p className="text-xs text-gray-500">B2C</p>
                      </div>
                    )}
                    {formData.stock_b2b > 0 && (
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-700">{formData.stock_b2b}</p>
                        <p className="text-xs text-gray-500">B2B</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {formData.description && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">תיאור</h4>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {formData.description}
                    </p>
                  </div>
                )}

                {/* Gallery */}
                {allImages.length > 1 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">גלריה</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {allImages.slice(1).map((image) => (
                        <img
                          key={image.id}
                          src={image.url || image.preview}
                          alt="Gallery"
                          className="w-full h-20 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t-2 border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-gray-700 font-semibold hover:bg-gray-200 rounded-xl transition-all"
            disabled={loading}
          >
            ביטול
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                שומר...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-6 w-6" />
                שמור שינויים
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProductModern;
